import React, { useEffect, useState } from 'react';
import Sidebar from '../../components/Sidebar';
import { useLanguage } from '../../contexts/LanguageContext';
import axios from 'axios';
import { Plus, X, Award } from 'lucide-react';
import { downloadPdf } from '../../utils/downloadPdf';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminLicenses() {
  const { t } = useLanguage();
  const [licenses, setLicenses] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ user_id: '', product: '', license_key: '', expiry_date: '', status: 'active', quantity: '1' });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/admin/licenses`).then(r => setLicenses(r.data)).catch(() => {});
    axios.get(`${API}/api/admin/users`).then(r => setUsers(r.data.filter((u: any) => u.role === 'client'))).catch(() => {});
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    try {
      const { data } = await axios.post(`${API}/api/admin/licenses`, {
        user_id: form.user_id, product: form.product, license_key: form.license_key,
        expiry_date: form.expiry_date, status: form.status, quantity: parseInt(form.quantity) || 1,
      });
      setLicenses([data, ...licenses]);
      setShowCreate(false);
      setForm({ user_id: '', product: '', license_key: '', expiry_date: '', status: 'active', quantity: '1' });
    } catch {} finally { setCreating(false); }
  };

  const inputStyle: React.CSSProperties = { width: '100%', padding: '10px 12px', border: '1px solid var(--border)', fontSize: 14, outline: 'none', boxSizing: 'border-box' as const };
  const labelStyle: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.06em' };

  const productOptions = [
    'Microsoft 365 Business Basic', 'Microsoft 365 Business Standard', 'Microsoft 365 Business Premium',
    'Microsoft 365 E3', 'Microsoft 365 E5', 'Office 365 E1', 'Office 365 E3', 'Office 365 E5',
    'Windows 10/11 Enterprise E3', 'Windows 10/11 Enterprise E5',
    'Adobe Creative Cloud All Apps', 'Adobe Photoshop', 'Adobe Illustrator', 'Adobe Acrobat Pro',
    'Kaspersky Endpoint Security', 'Kaspersky Small Office Security',
    '3CX Phone System Standard', '3CX Phone System Pro', '3CX Enterprise',
    'Ekahau AI Pro', 'Ekahau Survey', 'Ekahau Connect',
  ];

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      <Sidebar type="admin" active="licenses" />
      <main data-testid="admin-licenses-page" style={{ flex: 1, background: 'var(--bg-portal)', padding: 32 }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
            <h1 style={{ fontFamily: 'var(--font-heading)', fontSize: 28, fontWeight: 700 }}>{t('admin.licenses')}</h1>
            <button data-testid="create-license-btn" onClick={() => setShowCreate(true)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 20px', background: 'var(--primary)', color: 'white', border: 'none', fontWeight: 600, fontSize: 13, cursor: 'pointer', textTransform: 'uppercase' }}>
              <Plus size={16} /> Assign License
            </button>
          </div>

          {/* Create modal */}
          {showCreate && (
            <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.3)' }} onClick={() => setShowCreate(false)} />
              <div style={{ position: 'relative', background: 'white', border: '1px solid var(--border)', padding: 32, width: '100%', maxWidth: 480, zIndex: 51 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                  <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: 20, fontWeight: 700 }}>Assign License</h2>
                  <button onClick={() => setShowCreate(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={18} /></button>
                </div>
                <form onSubmit={handleCreate} data-testid="create-license-form">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <div>
                      <label style={labelStyle}>Client *</label>
                      <select data-testid="license-user-select" value={form.user_id} onChange={e => setForm({...form, user_id: e.target.value})} required style={{...inputStyle, cursor: 'pointer'}}>
                        <option value="">Select client...</option>
                        {users.map(u => <option key={u._id} value={u._id}>{u.name} ({u.email})</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={labelStyle}>Product *</label>
                      <select data-testid="license-product-select" value={form.product} onChange={e => setForm({...form, product: e.target.value})} required style={{...inputStyle, cursor: 'pointer'}}>
                        <option value="">Select product...</option>
                        {productOptions.map(p => <option key={p} value={p}>{p}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={labelStyle}>License Key</label>
                      <input data-testid="license-key-input" value={form.license_key} onChange={e => setForm({...form, license_key: e.target.value})} placeholder="XXXXX-XXXXX-XXXXX-XXXXX" style={inputStyle} />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                      <div>
                        <label style={labelStyle}>Expiry Date</label>
                        <input type="date" value={form.expiry_date} onChange={e => setForm({...form, expiry_date: e.target.value})} style={inputStyle} />
                      </div>
                      <div>
                        <label style={labelStyle}>Quantity</label>
                        <input type="number" min={1} value={form.quantity} onChange={e => setForm({...form, quantity: e.target.value})} style={inputStyle} />
                      </div>
                    </div>
                    <div>
                      <label style={labelStyle}>Status</label>
                      <select value={form.status} onChange={e => setForm({...form, status: e.target.value})} style={{...inputStyle, cursor: 'pointer'}}>
                        {['active', 'expired', 'suspended'].map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    <button type="submit" data-testid="license-submit-btn" disabled={creating}
                      style={{ padding: '12px', background: 'var(--primary)', color: 'white', fontWeight: 600, border: 'none', textTransform: 'uppercase', cursor: 'pointer', opacity: creating ? 0.6 : 1 }}>
                      {creating ? '...' : 'Assign License'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Table */}
          <div style={{ background: 'white', border: '1px solid var(--border)' }}>
            {licenses.length === 0 ? (
              <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted)' }}>{t('portal.no_licenses')}</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border)' }}>
                      {[t('portal.product'), 'User', 'Key', 'Qty', t('portal.status'), 'Expiry', 'Certificate'].map(h => (
                        <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {licenses.map(lic => (
                      <tr key={lic._id} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '12px 16px', fontSize: 14, fontWeight: 500 }}>{lic.product}</td>
                        <td style={{ padding: '12px 16px', fontSize: 12, color: 'var(--text-secondary)' }}>{lic.user_id}</td>
                        <td style={{ padding: '12px 16px', fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>{lic.license_key || '-'}</td>
                        <td style={{ padding: '12px 16px', fontSize: 13 }}>{lic.quantity || 1}</td>
                        <td style={{ padding: '12px 16px' }}>
                          <span style={{ padding: '3px 8px', fontSize: 11, fontWeight: 600, background: lic.status === 'active' ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)', color: lic.status === 'active' ? '#16A34A' : '#DC2626', textTransform: 'uppercase' }}>
                            {t(`status.${lic.status}`) || lic.status}
                          </span>
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: 12, color: 'var(--text-muted)' }}>{lic.expiry_date || '-'}</td>
                        <td style={{ padding: '12px 16px' }}>
                          <button onClick={() => downloadPdf(`/api/admin/licenses/${lic._id}/certificate`, `certificate-${lic._id.slice(-8)}.pdf`)} data-testid={`admin-cert-${lic._id}`}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', fontSize: 10, fontWeight: 600, background: 'rgba(13,71,161,0.08)', color: 'var(--primary)', border: '1px solid rgba(13,71,161,0.2)', cursor: 'pointer' }}>
                            <Award size={10} /> Certificate
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
